import type { ReactNode } from 'react'

/**
 * How a state reads at a glance, the one scale every status uses: header
 * pills, chips, and status text in grids. Working or done is success, in
 * progress or waiting its turn is info, held or needing a look is warning,
 * failed is danger, and inactive or not set up is neutral.
 */
export type StatusTone = 'success' | 'neutral' | 'info' | 'danger' | 'warning'
export type PageHeaderStatusTone = StatusTone

/** A state's dot color, shared by the header pill and the title switcher's list. */
export const PAGE_HEADER_TONE_DOT: Record<StatusTone, string> = {
  success: 'bg-[color:var(--color-success-500)]',
  neutral: 'bg-[color:var(--text-faint)]',
  info: 'bg-[color:var(--color-info-500)]',
  danger: 'bg-[color:var(--color-danger-500)]',
  warning: 'bg-[color:var(--color-warning-500)]',
}

/** A pill or chip's tint, border, and text by tone, themed (see `.status-tone-*`). */
export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  success: 'status-tone-success',
  neutral: 'status-tone-neutral',
  info: 'status-tone-info',
  danger: 'status-tone-danger',
  warning: 'status-tone-warning',
}

/**
 * An option's label by tone, such as Delete table in red: a choice colored by
 * what choosing it does. Neutral leaves the label in the default text color.
 */
export function optionToneTextClass(tone: StatusTone | undefined): string | undefined {
  return tone && tone !== 'neutral' ? STATUS_TONE_TEXT[tone] : undefined
}

/** A status written as plain text, such as in a grid cell, by tone. */
export const STATUS_TONE_TEXT: Record<StatusTone, string> = {
  success: 'text-success',
  neutral: 'text-muted',
  info: 'text-[color:var(--accent-info)]',
  danger: 'text-danger',
  warning: 'text-warning',
}

/**
 * One status beside a page title. Pages hand `PageHeader` these as data, so
 * every status renders as the same pill and nothing else can sit there.
 */
export interface PageHeaderStatusItem {
  label: string
  tone: PageHeaderStatusTone
  /** Regular-weight qualifier after the label, such as a version number. */
  qualifier?: ReactNode
}
